import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Hash, ChevronRight, Plus, Trash2, Loader2 } from 'lucide-react';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  useSection,
  useUpdateSection,
  useDeleteSection,
  useCreateSubsection,
} from '@/features/content/queries';
import { useAuth } from '@/features/auth/AuthProvider';
import { EditToolbar } from '@/features/content/EditToolbar';

interface Draft {
  titulo: string;
  descripcion: string;
  icono: string;
}

function SectionPage() {
  const { section: slug } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useSection(slug);
  const { user } = useAuth();
  const update = useUpdateSection();
  const remove = useDeleteSection();
  const createSub = useCreateSubsection();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({ titulo: '', descripcion: '', icono: '' });
  const [newSubOpen, setNewSubOpen] = useState(false);
  const [newSubTitle, setNewSubTitle] = useState('');

  useEffect(() => {
    if (data?.section) {
      setDraft({
        titulo: data.section.titulo,
        descripcion: data.section.descripcion ?? '',
        icono: data.section.icono ?? '',
      });
    }
  }, [data?.section]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-10 w-1/2" />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }
  if (!data?.section) return <div>Sección no encontrada</div>;
  const { section } = data;
  const isAdmin = user?.rol === 'admin';

  const save = async () => {
    await update.mutateAsync({
      id: section.id,
      data: {
        titulo: draft.titulo,
        descripcion: draft.descripcion,
        icono: draft.icono || undefined,
      },
    });
    setEditing(false);
  };

  const cancel = () => {
    setDraft({
      titulo: section.titulo,
      descripcion: section.descripcion ?? '',
      icono: section.icono ?? '',
    });
    setEditing(false);
  };

  const del = async () => {
    if (
      !confirm(
        `¿Eliminar la sección "${section.titulo}" con todas sus subsecciones y artículos? Esta acción no se puede deshacer.`,
      )
    )
      return;
    await remove.mutateAsync(section.id);
    navigate({ to: '/' });
  };

  const createSubsection = async () => {
    if (!newSubTitle.trim()) return;
    const res = await createSub.mutateAsync({
      sectionId: section.id,
      titulo: newSubTitle.trim(),
      contentMd: '',
    });
    setNewSubOpen(false);
    setNewSubTitle('');
    navigate({
      to: '/seccion/$section/$subsection',
      params: { section: section.slug, subsection: res.subsection.slug },
    });
  };

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: section.titulo }]} />

      {isAdmin && (
        <EditToolbar
          editing={editing}
          onEdit={() => setEditing(true)}
          onSave={save}
          onCancel={cancel}
          saving={update.isPending}
          viewActions={
            <>
              <Button size="sm" variant="brand" onClick={() => setNewSubOpen(true)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Nueva subsección
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={del}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Eliminar sección
              </Button>
            </>
          }
          editActions={
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive ml-auto"
              onClick={del}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Eliminar sección
            </Button>
          }
        />
      )}

      <header>
        {editing ? (
          <>
            <Input
              value={draft.titulo}
              onChange={(e) => setDraft({ ...draft, titulo: e.target.value })}
              placeholder="Título de la sección"
              className="h-auto border-0 bg-transparent px-0 text-3xl font-bold tracking-tight shadow-none focus-visible:ring-0"
            />
            <Textarea
              value={draft.descripcion}
              onChange={(e) => setDraft({ ...draft, descripcion: e.target.value })}
              placeholder="Descripción breve"
              rows={2}
              className="mt-2 resize-none border-0 px-0 text-base shadow-none focus-visible:ring-0"
            />
          </>
        ) : (
          <>
            <h1 className="text-3xl font-bold tracking-tight">{section.titulo}</h1>
            {section.descripcion && (
              <p className="text-muted-foreground mt-1 max-w-2xl">{section.descripcion}</p>
            )}
          </>
        )}
      </header>

      <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {section.subsections.map((sub) => (
          <Link
            key={sub.id}
            to="/seccion/$section/$subsection"
            params={{ section: slug, subsection: sub.slug }}
          >
            <Card className="hover:border-brand/40 group h-full transition-colors">
              <CardHeader>
                <div className="text-muted-foreground mb-1 flex items-center gap-1.5 text-xs">
                  <Hash className="h-3 w-3" />
                  Subsección
                </div>
                <CardTitle className="group-hover:text-brand flex items-center justify-between text-lg transition-colors">
                  {sub.titulo}
                  <ChevronRight className="text-muted-foreground h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </CardTitle>
                {sub.descripcion && <CardDescription>{sub.descripcion}</CardDescription>}
              </CardHeader>
              <CardContent className="text-muted-foreground text-xs">
                Abrir para ver el contenido y los artículos relacionados.
              </CardContent>
            </Card>
          </Link>
        ))}
        {section.subsections.length === 0 && (
          <div className="bg-muted/30 text-muted-foreground col-span-full rounded-lg border p-6 text-center text-sm">
            Esta sección aún no tiene subsecciones.{' '}
            {isAdmin && 'Crea una con el botón “Nueva subsección” arriba.'}
          </div>
        )}
      </section>

      <Dialog open={newSubOpen} onOpenChange={setNewSubOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nueva subsección</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Título</label>
            <Input
              autoFocus
              value={newSubTitle}
              onChange={(e) => setNewSubTitle(e.target.value)}
              placeholder="Ej. Devoluciones"
              onKeyDown={(e) => {
                if (e.key === 'Enter') createSubsection();
              }}
            />
            <p className="text-muted-foreground text-xs">
              Se creará dentro de “{section.titulo}”. Después podrás editar contenido y agregar
              artículos.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewSubOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={createSubsection} disabled={createSub.isPending || !newSubTitle.trim()}>
              {createSub.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Crear y abrir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Route = createFileRoute('/seccion/$section')({ component: SectionPage });
